<script lang="ts" setup>
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	SingleSelectList,
	SingleSelectListItem,
} from '@laam/ui/base';

import CustomShirtFigure from './CustomShirtFigure.vue';
import { useField } from 'vee-validate';

interface CustomShirtProps {
	customShirt: {
		[key: string]: string;
	};
}
const props = defineProps<CustomShirtProps>();

const units = useField<string | undefined>('unitsShirt').value;

const lengthHighlighted = ref(false);
const shoulderHighlighted = ref(false);
const chestHighlighted = ref(false);
const waistHighlighted = ref(false);
const hipHighlighted = ref(false);
const bottomHighlighted = ref(false);
const sleeveHighlighted = ref(false);
const armHoleHighlighted = ref(false);
const wristHighlighted = ref(false);

function sendval(e: { target: { id: string; value: string } }) {
	console.log('e.target.value', e.target.value);
}
</script>

<template>
	<div class="size-chart__custom-shirt-form">
		<div class="mb-xl flex flex-row justify-between">
			<span class="block text-2xl font-medium">Shirt</span>
			<FormField v-slot="{ componentField }" name="unitsShirt">
				<FormItem>
					<FormControl>
						<SingleSelectList
							v-bind="componentField"
							class="grid grid-cols-2"
							default-value="Cm"
						>
							<SingleSelectListItem value="Cm"> Cm </SingleSelectListItem>
							<SingleSelectListItem value="In"> In </SingleSelectListItem>
						</SingleSelectList>
					</FormControl>
				</FormItem>
			</FormField>
		</div>

		<CustomShirtFigure
			:custom-shirt="props.customShirt"
			:length-highlighted="lengthHighlighted"
			:shoulder-highlighted="shoulderHighlighted"
			:chest-highlighted="chestHighlighted"
			:waist-highlighted="waistHighlighted"
			:hip-highlighted="hipHighlighted"
			:bottom-highlighted="bottomHighlighted"
			:sleeve-highlighted="sleeveHighlighted"
			:arm-hole-highlighted="armHoleHighlighted"
			:wrist-highlighted="wristHighlighted"
		/>

		<div class="-mx-3xl border-b border-gray-200 border-solid"></div>

		<div class="py-3xl custom_shirt_form gap-3xl flex flex-col w-full">
			<FormField v-slot="{ componentField }" name="shirtLength">
				<FormItem class="form-item">
					<FormLabel for="Shirt Length" class="form-label">
						Shirt Length({{ units }})
					</FormLabel>
					<FormControl>
						<Input
							v-bind="componentField"
							id="Shirt Length"
							type="number"
							class="form-input"
							@focus="lengthHighlighted = true"
							@blur="lengthHighlighted = false"
							@input="sendval"
						/>
					</FormControl>
					<FormMessage />
				</FormItem>
			</FormField>

			<FormField v-slot="{ componentField }" name="shoulderLength">
				<FormItem class="form-item">
					<FormControl>
						<FormLabel for="Shirt Shoulder" class="form-label">
							Shoulder({{ units }})
						</FormLabel>
						<Input
							v-bind="componentField"
							id="Shirt Shoulder"
							type="number"
							class="form-input"
							@focus="shoulderHighlighted = true"
							@blur="shoulderHighlighted = false"
							@input="sendval"
						/>
					</FormControl>
					<FormMessage />
				</FormItem>
			</FormField>

			<FormField v-slot="{ componentField }" name="armHoleLength">
				<FormItem class="form-item">
					<FormLabel for="Shirt Arm Hole" class="form-label">
						Arm Hole({{ units }})
					</FormLabel>
					<FormControl>
						<Input
							v-bind="componentField"
							id="Shirt Arm Hole"
							type="number"
							class="form-input"
							@focus="armHoleHighlighted = true"
							@blur="armHoleHighlighted = false"
							@input="sendval"
						/>
					</FormControl>
					<FormMessage />
				</FormItem>
			</FormField>

			<FormField v-slot="{ componentField }" name="chestLength">
				<FormItem class="form-item">
					<FormLabel for="Shirt Chest" class="form-label">
						Chest({{ units }})
					</FormLabel>
					<FormControl>
						<Input
							v-bind="componentField"
							id="Shirt Chest"
							type="number"
							class="form-input"
							@focus="chestHighlighted = true"
							@blur="chestHighlighted = false"
							@input="sendval"
						/>
					</FormControl>
					<FormMessage />
				</FormItem>
			</FormField>

			<FormField v-slot="{ componentField }" name="waistLength">
				<FormItem class="form-item">
					<FormLabel for="Shirt Waist" class="form-label">
						Waist({{ units }})
					</FormLabel>
					<FormControl>
						<Input
							v-bind="componentField"
							id="Shirt Waist"
							type="number"
							class="form-input"
							@focus="waistHighlighted = true"
							@blur="waistHighlighted = false"
							@input="sendval"
						/>
					</FormControl>
					<FormMessage />
				</FormItem>
			</FormField>

			<FormField v-slot="{ componentField }" name="hipLength">
				<FormItem class="form-item">
					<FormLabel for="Shirt Hip" class="form-label">
						Hip({{ units }})
					</FormLabel>
					<FormControl>
						<Input
							v-bind="componentField"
							id="Shirt Hip"
							type="number"
							class="form-input"
							@focus="hipHighlighted = true"
							@blur="hipHighlighted = false"
							@input="sendval"
						/>
					</FormControl>
					<FormMessage />
				</FormItem>
			</FormField>

			<FormField v-slot="{ componentField }" name="bottomLength">
				<FormItem class="form-item">
					<FormLabel for="Shirt Bottom" class="form-label">
						Bottom/Daaman({{ units }})
					</FormLabel>
					<FormControl>
						<Input
							v-bind="componentField"
							id="Shirt Bottom"
							type="number"
							class="form-input"
							@focus="bottomHighlighted = true"
							@blur="bottomHighlighted = false"
							@input="sendval"
						/>
					</FormControl>
					<FormMessage />
				</FormItem>
			</FormField>

			<FormField v-slot="{ componentField }" name="sleeveLength">
				<FormItem class="form-item">
					<FormLabel for="Shirt Sleeve Length" class="form-label">
						Sleeve({{ units }})
					</FormLabel>
					<FormControl>
						<Input
							v-bind="componentField"
							id="Shirt Sleeve Length"
							type="number"
							class="form-input"
							@focus="sleeveHighlighted = true"
							@blur="sleeveHighlighted = false"
							@input="sendval"
						/>
					</FormControl>
					<FormMessage />
				</FormItem>
			</FormField>

			<FormField v-slot="{ componentField }" name="wristLength">
				<FormItem class="form-item">
					<FormLabel for="Shirt Wrist" class="form-label">
						Wrist Opening ({{ units }})
					</FormLabel>
					<FormControl>
						<Input
							v-bind="componentField"
							id="Shirt Wrist"
							type="number"
							class="form-input"
							@focus="wristHighlighted = true"
							@blur="wristHighlighted = false"
							@input="sendval"
						/>
					</FormControl>

					<FormMessage />
				</FormItem>
			</FormField>
		</div>
	</div>
</template>

<style>
.form-item {
	@apply flex flex-col gap-sm;
}

.form-label {
	@apply font-normal text-gray-800 text-start;
}

.form-input {
	@apply border block w-full appearance-none rounded  border-solid border-[#C6C6C6] bg-transparent px-lg py-md  text-sm font-normal text-[#888888] valid:border-green-500 focus:border-rose-600 focus:outline-none focus:ring-0;
}
</style>
